import React, { useState } from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard, CalendarDays, Users, FileText, Wallet, ShieldCheck, Receipt,
  LogOut, Menu, X, Leaf, Settings, QrCode, Mail,
} from "lucide-react";

const ADMIN_EMAIL = "valerie@capsulepatrimoine.com";

const navItems = [
  { to: "/dashboard", label: "Tableau de bord", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/agenda", label: "Agenda", icon: CalendarDays, testid: "nav-agenda" },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
  { to: "/facturation", label: "Facturation", icon: FileText, testid: "nav-facturation" },
  { to: "/depenses", label: "Dépenses", icon: Receipt, testid: "nav-depenses" },
  { to: "/finances", label: "Mes finances", icon: Wallet, testid: "nav-finances" },
  { to: "/protection", label: "Ma protection", icon: ShieldCheck, testid: "nav-protection" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  const initials = (user?.full_name || "P")
    .split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: "var(--bg)" }}>
      {/* Sidebar — desktop */}
      <aside
        className="hidden lg:flex w-72 flex-col border-r p-6"
        style={{ backgroundColor: "var(--sidebar)", borderColor: "var(--border)" }}
        data-testid="sidebar-desktop"
      >
        <Link to="/dashboard" className="flex items-center gap-3 mb-10">
          <div className="w-10 h-10 rounded-2xl bg-sage flex items-center justify-center">
            <Leaf className="w-5 h-5 text-white" strokeWidth={1.6} />
          </div>
          <div>
            <div className="font-display text-base leading-tight">Mon Cabinet</div>
            <div className="text-xs text-muted-soft">Bien-être</div>
          </div>
        </Link>

        <nav className="flex flex-col gap-1 flex-1">
          {navItems.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-sm ${
                  isActive
                    ? "bg-sage text-white font-medium shadow-[0_4px_18px_rgba(123,140,122,0.25)]"
                    : "text-text-primary hover:bg-sage-light"
                }`
              }
              style={({ isActive }) => isActive ? { color: "white" } : { color: "var(--text-primary)" }}
            >
              <Icon className="w-5 h-5" strokeWidth={1.5} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="mt-6 pt-6 border-t" style={{ borderColor: "var(--border)" }}>
          {user?.email === ADMIN_EMAIL && (
            <Link to="/prospects" data-testid="nav-prospects"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm hover:bg-sage-light transition-all">
              <Mail className="w-4 h-4 text-sage" strokeWidth={1.5} />
              <span>Prospects</span>
              <span className="ml-auto text-[10px] text-sage font-medium uppercase tracking-wider">Admin</span>
            </Link>
          )}
          <Link to="/install" data-testid="nav-install-app"
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm hover:bg-sage-light transition-all">
            <QrCode className="w-4 h-4 text-sage" strokeWidth={1.5} />
            <span>Installer sur smartphone</span>
          </Link>
          <Link to="/settings" data-testid="nav-settings"
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm hover:bg-sage-light transition-all">
            <Settings className="w-4 h-4" strokeWidth={1.5} />
            Paramètres
          </Link>
          <div className="flex items-center gap-3 px-4 py-3 mt-1">
            <div className="w-9 h-9 rounded-full bg-beige flex items-center justify-center text-sm font-medium" style={{ color: "#3A352D" }}>
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" data-testid="user-name">{user?.full_name}</div>
              <div className="text-xs text-muted-soft truncate">{user?.profession}</div>
            </div>
            <button
              data-testid="logout-button"
              onClick={onLogout}
              className="p-2 rounded-full hover:bg-sage-light transition-all"
              title="Déconnexion"
            >
              <LogOut className="w-4 h-4 text-muted-soft" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 p-6 flex flex-col"
            style={{ backgroundColor: "var(--sidebar)" }}>
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-sage flex items-center justify-center">
                  <Leaf className="w-5 h-5 text-white" strokeWidth={1.6} />
                </div>
                <div className="font-display">Mon Cabinet</div>
              </div>
              <button onClick={() => setOpen(false)} className="p-2"><X className="w-5 h-5" /></button>
            </div>
            <nav className="flex flex-col gap-1">
              {navItems.map(({ to, label, icon: Icon, testid }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-${testid}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-xl text-sm ${
                      isActive ? "bg-sage text-white" : "hover:bg-sage-light"
                    }`
                  }
                >
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                  {label}
                </NavLink>
              ))}
            </nav>
            <button
              data-testid="mobile-logout"
              onClick={onLogout}
              className="mt-auto flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-clay hover:bg-sage-light"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
              Déconnexion
            </button>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar mobile */}
        <header className="lg:hidden flex items-center justify-between px-4 h-16 border-b sticky top-0 z-30 backdrop-blur"
          style={{ backgroundColor: "rgba(250,249,246,0.85)", borderColor: "var(--border)" }}>
          <button onClick={() => setOpen(true)} data-testid="open-mobile-menu" className="p-2">
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-sage flex items-center justify-center">
              <Leaf className="w-4 h-4 text-white" strokeWidth={1.6} />
            </div>
            <div className="font-display text-sm">Mon Cabinet</div>
          </div>
          <div className="w-9 h-9 rounded-full bg-beige flex items-center justify-center text-xs font-medium" style={{ color: "#3A352D" }}>
            {initials}
          </div>
        </header>

        <main className="flex-1 px-5 sm:px-8 lg:px-10 py-6 lg:py-10 max-w-[1400px] w-full mx-auto fade-up">
          {children}
        </main>

        <footer className="px-6 py-6 text-xs text-muted-soft text-center border-t"
          style={{ borderColor: "var(--border)" }}>
          Mon Cabinet Bien-être — by Capsule Patrimoine · Créé par Valérie Girin · SIRET 484 280 771 00060
        </footer>
      </div>
    </div>
  );
}
